import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { triggerDownload } from '@/lib/download'
import type { DownloadedCallRecording, RecordingType } from '../api'

interface CallRecordingDownloadButtonProps {
  /** Falsy when the call has no recording at all — hides the button. */
  hasRecording: boolean
  /**
   * Which recording variant to download. `null` while `useCallDetail`'s
   * query hasn't settled yet, or once it has settled with nothing to
   * download (mirrors `recordingBlocked`). Unlike the playback signed URL,
   * this needs no network round trip — it comes straight off the call detail
   * payload — so it's what the button waits on rather than a resolved URL.
   */
  recordingType: RecordingType | null
  /** From `useCallDetail`: the detail query settled with no way to get a URL. */
  recordingBlocked?: boolean
  /**
   * Fetches the recording as a blob through the same-origin
   * `/recording-download` proxy — see `useCallDetail`'s `downloadRecording`
   * and `downloadCallRecording` in `../api` for why this button doesn't just
   * follow the cached `resolvedRecordingUrl` `AudioPlayer` plays:
   *
   * 1. That URL can go stale — R2/Vapi signed URLs are short-lived, and
   *    nothing re-resolves it just because the sheet stayed open.
   *    `downloadRecording` always asks the backend for a fresh one.
   * 2. Vapi/R2's signed URLs are cross-origin and carry neither
   *    `Content-Disposition: attachment` nor CORS headers permissive enough
   *    for a browser `fetch()` — pointing `<a download>` straight at one is
   *    unreliable and can navigate the tab to the raw audio instead of
   *    saving a file. Routing through our own backend sidesteps both: the
   *    fetch happens server-side (no CORS gate) and the response comes back
   *    same-origin with the attachment header already set.
   */
  downloadRecording: () => Promise<DownloadedCallRecording | null>
}

/**
 * Small icon button beside the "Recording" section title (see
 * `CallLogsSheetDetails`) that downloads the call's audio file.
 *
 * Hidden entirely when there's nothing to download — no recording
 * (`hasRecording` false) or the recording is known unavailable
 * (`recordingBlocked`) — mirroring the gating `CallLogsRecording` applies to
 * the player itself. While a recording exists but its type hasn't been
 * determined yet, the button stays visible but disabled rather than popping
 * in once the sheet's call detail fetch (see `CallLogsSheetWrapper`) lands.
 */
export function CallRecordingDownloadButton({
  hasRecording,
  recordingType,
  recordingBlocked = false,
  downloadRecording,
}: CallRecordingDownloadButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false)

  if (!hasRecording || recordingBlocked) return null

  const isReady = Boolean(recordingType)

  const handleDownload = async () => {
    if (!isReady || isDownloading) return
    setIsDownloading(true)
    try {
      const downloaded = await downloadRecording()
      if (!downloaded) {
        toast.error('Recording is not available to download')
        return
      }
      // A blob: URL is same-origin by construction, so `download` is honoured
      // regardless of where the bytes actually came from — unlike the raw
      // signed URL, which is why we fetched a blob instead of linking to it.
      const blobUrl = URL.createObjectURL(downloaded.blob)
      try {
        triggerDownload(blobUrl, downloaded.filename)
      } finally {
        // Deferred by a tick rather than revoked immediately: some browsers
        // (historically Firefox) read the blob asynchronously after the
        // click is dispatched, and revoking the URL before that read
        // completes can silently cancel the download.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 0)
      }
    } catch {
      toast.error('Failed to download recording')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      disabled={!isReady || isDownloading}
      onClick={() => void handleDownload()}
      aria-label="Download recording"
      title={
        isDownloading
          ? 'Downloading…'
          : isReady
            ? 'Download recording'
            : 'Preparing download…'
      }>
      {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
    </Button>
  )
}
